import { System } from '@ecs/System';
import { Entity } from '@ecs/Entity';
import { Position } from '@components/Position';
import { Collider } from '@components/Collider';
import { Health } from '@components/Health';
import { Shield } from '@components/Shield';
import { Tag } from '@components/Tag';
import { PlayerInput } from '@components/PlayerInput';
import { Damage } from '@components/Damage';
import { Torpedo } from '@components/Torpedo';
import { Renderable } from '@components/Renderable';
import { ShieldDisplay } from '@components/ShieldDisplay';
import { HitFlash } from '@components/HitFlash';
import { ScoreValue } from '@components/ScoreValue';
import { ExplosionRequest } from '@components/ExplosionRequest';
import { ShieldImpactRequest } from '@components/ShieldImpactRequest';
import { UIElement } from '@components/UIElement';
import { addCameraTrauma } from '@utils/CameraUtils';

// Enemies with more health than this survive ramming a player and get knocked back instead
const RAM_DAMAGE_TO_ENEMY = 2;
const RAM_KNOCKBACK = 20;
const PLAYER_COLOR = '#0066ff';

export class CollisionLogicSystem extends System {
  private score: number = 0;
  private enemiesKilled: number = 0;
  private onEnemyKilled?: () => void;
  private onPlayerShipLost?: (ship: Entity) => void;

  update(_deltaTime: number): void {
    const entities = this.getEntitiesWithComponents(Position, Collider);

    // Check all pairs of entities for collision. Entities destroyed earlier in
    // this frame are skipped so a single bullet or enemy is only processed once.
    for (let i = 0; i < entities.length; i++) {
      if (this.world.isPendingRemoval(entities[i])) continue;
      for (let j = i + 1; j < entities.length; j++) {
        if (this.world.isPendingRemoval(entities[j])) continue;
        this.checkCollision(entities[i], entities[j]);
        if (this.world.isPendingRemoval(entities[i])) break;
      }
    }

    // Update UI elements with current score
    this.updateUIElements();
  }

  private checkCollision(entityA: Entity, entityB: Entity): void {
    const posA = entityA.getComponent(Position)!;
    const posB = entityB.getComponent(Position)!;
    const colliderA = entityA.getComponent(Collider)!;
    const colliderB = entityB.getComponent(Collider)!;

    // Check if these entities can collide
    if (!colliderA.collidesWith.includes(colliderB.group) &&
        !colliderB.collidesWith.includes(colliderA.group)) {
      return;
    }

    // Check distance
    const dx = posA.x - posB.x;
    const dy = posA.y - posB.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < colliderA.radius + colliderB.radius) {
      this.handleCollision(entityA, entityB);
    }
  }

  private handleCollision(entityA: Entity, entityB: Entity): void {
    const groupA = entityA.getComponent(Collider)!.group;
    const groupB = entityB.getComponent(Collider)!.group;
    const pick = (group: string): Entity => (groupA === group ? entityA : entityB);
    const is = (first: string, second: string): boolean =>
      (groupA === first && groupB === second) || (groupA === second && groupB === first);

    if (is('playerBullet', 'enemy')) {
      this.handleBulletEnemyCollision(pick('playerBullet'), pick('enemy'));
    } else if (is('enemyBullet', 'player')) {
      this.handleBulletPlayerCollision(pick('enemyBullet'), pick('player'));
    } else if (is('enemy', 'player')) {
      this.handleEnemyPlayerCollision(pick('enemy'), pick('player'));
    } else if (is('playerBullet', 'enemyBullet')) {
      // Only mines opt in to being shot (see EntityFactory.createMine)
      const target = pick('enemyBullet');
      if (target.getComponent(Tag)?.value === 'mine') {
        this.handlePointDefenseCollision(pick('playerBullet'), target);
      }
    } else if (is('pointDefenseBullet', 'enemyBullet')) {
      const target = pick('enemyBullet');
      const tag = target.getComponent(Tag)?.value;
      if (tag === 'torpedo' || tag === 'mine') {
        this.handlePointDefenseCollision(pick('pointDefenseBullet'), target);
      }
    }
  }

  /**
   * Applies damage to shields first, then health.
   * Returns true when the target has been destroyed.
   */
  private applyDamage(target: Entity, amount: number, hitAngle: number): boolean {
    const position = target.getComponent(Position)!;
    let damage = amount;

    const shield = target.getComponent(Shield);
    if (shield && shield.isActive) {
      damage = shield.takeDamage(damage);
      if (damage < amount) {
        const color = target.getComponent(ShieldDisplay)?.shieldColor ?? '#00ffff';
        target.addComponent(new ShieldImpactRequest(position.x, position.y, hitAngle, 6, color));
      }
    }

    if (damage <= 0) return false;

    const health = target.getComponent(Health);
    if (!health) return true;

    health.current = Math.max(0, health.current - damage);
    if (health.current <= 0) return true;

    target.addComponent(new HitFlash());
    return false;
  }

  private handleBulletEnemyCollision(bullet: Entity, enemy: Entity): void {
    const bulletPos = bullet.getComponent(Position)!;
    const enemyPos = enemy.getComponent(Position)!;

    this.world.removeEntity(bullet);

    const damage = bullet.getComponent(Damage)?.amount ?? 1;
    const hitAngle = Math.atan2(bulletPos.y - enemyPos.y, bulletPos.x - enemyPos.x);
    const destroyed = this.applyDamage(enemy, damage, hitAngle);

    if (destroyed) {
      this.destroyEnemy(enemy);
    } else if (!enemy.hasComponent(ShieldImpactRequest)) {
      // Hull hit that didn't kill - small spark burst in the hull colour
      const color = enemy.getComponent(Renderable)?.color ?? '#ff4444';
      enemy.addComponent(new ExplosionRequest(bulletPos.x, bulletPos.y, 6, color, 2, 2, '#ffffff'));
    }
  }

  private handleBulletPlayerCollision(bullet: Entity, player: Entity): void {
    const bulletPos = bullet.getComponent(Position)!;
    const playerPos = player.getComponent(Position)!;

    this.world.removeEntity(bullet);

    const damage = bullet.getComponent(Damage)?.amount ?? bullet.getComponent(Torpedo)?.damage ?? 1;

    // Torpedoes and mines detonate visibly on impact
    const tag = bullet.getComponent(Tag)?.value;
    if (tag === 'torpedo' || tag === 'mine') {
      const color = bullet.getComponent(Renderable)?.color ?? '#ff8800';
      bullet.addComponent(new ExplosionRequest(bulletPos.x, bulletPos.y, 14, color, 3, 3, '#ffee88'));
    }

    const hitAngle = Math.atan2(bulletPos.y - playerPos.y, bulletPos.x - playerPos.x);
    this.damagePlayer(player, damage, hitAngle);
  }

  private handleEnemyPlayerCollision(enemy: Entity, player: Entity): void {
    const enemyPos = enemy.getComponent(Position)!;
    const playerPos = player.getComponent(Position)!;
    const angleToEnemy = Math.atan2(enemyPos.y - playerPos.y, enemyPos.x - playerPos.x);

    const ramDamage = enemy.getComponent(Damage)?.amount ?? 1;
    this.damagePlayer(player, ramDamage, angleToEnemy);

    const destroyed = this.applyDamage(enemy, RAM_DAMAGE_TO_ENEMY, angleToEnemy + Math.PI);
    if (destroyed) {
      this.destroyEnemy(enemy);
      return;
    }

    // Heavy ships survive the ram and get shoved clear so they don't re-collide every frame
    const enemyRadius = enemy.getComponent(Collider)!.radius;
    const playerRadius = player.getComponent(Collider)!.radius;
    const separation = enemyRadius + playerRadius + RAM_KNOCKBACK;
    enemyPos.x = playerPos.x + Math.cos(angleToEnemy) * separation;
    enemyPos.y = playerPos.y + Math.sin(angleToEnemy) * separation;
  }

  private damagePlayer(player: Entity, damage: number, hitAngle: number): void {
    const destroyed = this.applyDamage(player, damage, hitAngle);
    addCameraTrauma(this.world, destroyed ? 0.6 : 0.25);

    if (destroyed) {
      const playerPos = player.getComponent(Position)!;
      player.addComponent(new ExplosionRequest(
        playerPos.x, playerPos.y, 30, PLAYER_COLOR, 5, 4, '#aaddff'
      ));
      this.world.removeEntity(player);
      this.onPlayerShipLost?.(player);
    }
  }

  private destroyEnemy(enemy: Entity): void {
    const enemyPos = enemy.getComponent(Position)!;
    const radius = enemy.getComponent(Collider)?.radius ?? 8;
    const color = enemy.getComponent(Renderable)?.color ?? '#ff4444';
    const isLarge = radius >= 15;

    this.world.removeEntity(enemy);
    enemy.addComponent(new ExplosionRequest(
      enemyPos.x, enemyPos.y, isLarge ? 36 : 20, color, isLarge ? 5 : 4, isLarge ? 4 : 3, '#ffaa00'
    ));
    if (isLarge) addCameraTrauma(this.world, 0.35);

    this.score += enemy.getComponent(ScoreValue)?.points ?? 100;
    this.enemiesKilled++;

    // Clear target if destroyed
    const players = this.world.getEntitiesWithComponents(PlayerInput);
    for (const player of players) {
      const input = player.getComponent(PlayerInput)!;
      if (input.targetEntity === enemy.id) {
        input.targetEntity = null;
      }
    }

    // Notify level manager
    if (this.onEnemyKilled) {
      this.onEnemyKilled();
    }
  }

  /** A defensive shot hitting a torpedo or mine. */
  private handlePointDefenseCollision(pdBullet: Entity, target: Entity): void {
    const targetPos = target.getComponent(Position)!;

    // Remove point defense bullet
    this.world.removeEntity(pdBullet);

    const health = target.getComponent(Health);
    if (health) {
      health.current -= pdBullet.getComponent(Damage)?.amount ?? 1;
    }

    if (!health || health.current <= 0) {
      // Target destroyed - request intercept explosion
      target.addComponent(new ExplosionRequest(
        targetPos.x, targetPos.y, 12, '#ffff00', 3, 3, '#ff8800'
      ));
      this.world.removeEntity(target);
    } else {
      // Damaged but still alive - small impact
      target.addComponent(new ExplosionRequest(
        targetPos.x, targetPos.y, 4, '#ffff00', 2, 2, '#ffffff'
      ));
    }
  }

  private updateUIElements(): void {
    // Create or update UI element for score
    let scoreUIEntity = this.world.getEntitiesWithComponents(UIElement)
      .find(e => e.getComponent(UIElement)?.elementId === 'score');

    if (!scoreUIEntity) {
      scoreUIEntity = new Entity()
        .addComponent(new UIElement('score', this.score));
      this.world.addEntity(scoreUIEntity);
    } else {
      const uiElement = scoreUIEntity.getComponent(UIElement)!;
      if (uiElement.value !== this.score) {
        uiElement.value = this.score;
        uiElement.isDirty = true;
      }
    }
  }

  getScore(): number {
    return this.score;
  }

  getEnemiesKilled(): number {
    return this.enemiesKilled;
  }

  resetStats(): void {
    this.score = 0;
    this.enemiesKilled = 0;
  }

  setEnemyKilledCallback(callback: () => void): void {
    this.onEnemyKilled = callback;
  }

  setPlayerShipLostCallback(callback: (ship: Entity) => void): void {
    this.onPlayerShipLost = callback;
  }
}
